import { useEffect, useEffectEvent, useRef } from "react";

/** What closed the panel: Escape, a press outside it, or focus moving outside it. */
export type DismissReason = "escape" | "pointer" | "focus";

/** Spread on the panel's element, so the hook tells a press or a focus inside it from one outside. */
export interface OutsideDismissProps {
  onPointerDownCapture: () => void;
  onFocusCapture: () => void;
  onBlurCapture: () => void;
}

/**
 * Closes a panel that floats over the page without trapping focus, while it
 * is `open`: on Escape, on a press anywhere outside it, and when focus moves
 * outside it (Tab past its last item). "Inside" follows React's tree rather
 * than the DOM's, so the account menu and the tooltips, which Radix renders
 * in portals at the end of the page, count as inside the panel.
 *
 * An Escape that an open menu or tooltip has used already (Radix marks it
 * with `preventDefault`) is left alone: the first Escape closes the menu, the
 * next one the panel. A press works the same way. While a menu is open Radix
 * turns the page's pointer events off, so a press anywhere but the menu lands
 * on `<html>`, out of React's reach, even on the panel itself: such a press
 * closes only the menu, and the next one is judged as usual.
 */
export function useOutsideDismiss(
  open: boolean,
  onDismiss: (reason: DismissReason) => void,
): OutsideDismissProps {
  // Set by the panel's own capture handlers, which run before the document's.
  const pointerInside = useRef(false);
  const focusInside = useRef(false);
  const dismiss = useEffectEvent(onDismiss);

  useEffect(() => {
    if (!open) {
      return;
    }
    // The press that opened the panel left this set; only presses from now on count.
    pointerInside.current = false;
    const onPointerDown = (event: PointerEvent) => {
      // A press that reached no element of the page belongs to the open menu.
      const onPage = event.target !== document.documentElement;
      if (!pointerInside.current && onPage) {
        dismiss("pointer");
      }
      pointerInside.current = false;
    };
    const onFocusIn = () => {
      if (!focusInside.current) {
        dismiss("focus");
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        dismiss("escape");
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return {
    onPointerDownCapture: () => {
      pointerInside.current = true;
    },
    onFocusCapture: () => {
      focusInside.current = true;
    },
    onBlurCapture: () => {
      focusInside.current = false;
    },
  };
}
